import React, { ReactElement, useCallback, useEffect, useMemo } from 'react';

import { WizardPhase, WizardStep } from '../../../types';
import { useWizardMultistep, useWizardNavigation } from '../../../hooks';

type WizardMultistepProps = {
    children: ReactElement;
    id: WizardStep;
    phaseIndex: WizardPhase;
    nextStepId?: WizardStep;
    initiallyCompleted?: boolean;
    onNextPress?: () => boolean;
    onNextPressAsync?: () => Promise<boolean>;
};

export type InjectedWizardStepProps = {
    completeStep?: (completed: boolean) => void;
    setNextStepId?: (id: WizardStep) => void;
    stepId?: WizardStep;
    nextStepId?: WizardStep;
};

function WizardStepComponent({
    children,
    id,
    onNextPress,
    onNextPressAsync,
    nextStepId,
}: WizardMultistepProps): ReactElement<'div'> | null {
    const { setStepCompleted, setNextStepId } = useWizardMultistep();
    const { registerListener } = useWizardNavigation();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        if (!onNextPress) {
            return;
        }
        const listener = registerListener(id, 'navigateNext', onNextPress);
        return () => {
            listener.remove();
        };
    }, [onNextPress]);

    useEffect(() => {
        if (!onNextPressAsync) {
            return;
        }
        const listener = registerListener(id, 'navigateNext', onNextPressAsync);
        return () => {
            listener.remove();
        };
    }, [onNextPressAsync]);

    const handleCompleteStep = useCallback(
        (completed: boolean) => {
            setStepCompleted(id, completed);
        },
        [id, setStepCompleted],
    );

    const handleSetNextStepId = useCallback(
        (nextStepId: WizardStep) => {
            setNextStepId(id, nextStepId);
        },
        [id, setNextStepId],
    );

    const injectedProps = useMemo(
        () => ({
            completeStep: handleCompleteStep,
            setNextStepId: handleSetNextStepId,
            stepId: id,
            nextStepId: nextStepId,
        }),
        [handleCompleteStep, handleSetNextStepId, nextStepId],
    );

    return <>{React.cloneElement(children, injectedProps)}</>;
}

export default WizardStepComponent;
